import { StripHTML } from '@/utils/utils';
import ShortableText from 'components/common/ShortableText';
import PreviewQuestion from 'components/Questionsv2/PreviewQuestion';
import QuestionShortDescription from 'components/Questionsv2/QuestionShortDescription';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  hasLanguageKeyAndStringArrayValue,
  type TAnyClientQuestionType,
  type TAvailableLanguageCodes,
} from '@hubertai/screening-question-types';
import type { RootState } from 'app/store';
import { useAppSelector } from 'rtk/query/hooks';
import { selectTemplateOptions } from 'rtk/TemplateSelector';
import { applyTemplateOptionsToQuestion } from 'utils/questionTemplateOptions';

function getPreferredLanguage(
  available: TAnyClientQuestionType['available_languages'],
  templateLanguage?: TAvailableLanguageCodes,
) {
  if (templateLanguage && available.includes(templateLanguage)) {
    return templateLanguage;
  }
  if (available.includes('en-gb')) return 'en-gb';
  if (available.includes('en-us')) return 'en-us';
  return available[0];
}

function getQuestionText(text: TAnyClientQuestionType['text'], language: TAvailableLanguageCodes, missingText: string) {
  if (!text) return missingText;

  if (hasLanguageKeyAndStringArrayValue(text, language)) {
    return StripHTML(text[language].join(' '));
  }
  console.log('getQuestionText', text);
  return missingText + ' 2';
}

interface Props extends ComponentProps<'div'> {
  question: TAnyClientQuestionType;
  language?: TAvailableLanguageCodes;
}

/**
 *  - show (any) English if exists
 *  - otherwise the first available
 */
const QuestionTableView = ({ question, language: propLanguage }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const [language, setLanguage] = useState<TAvailableLanguageCodes>();
  const [questionText, setQuestionText] = useState<string>();
  const templateOptions = useAppSelector((state: RootState) =>
    question.id ? selectTemplateOptions(state, question.id) : undefined,
  );

  const questionConfig = useMemo(() => {
    if (!language) return question;
    return applyTemplateOptionsToQuestion(question, templateOptions, language);
  }, [question, templateOptions, language]);

  useEffect(() => {
    setLanguage(getPreferredLanguage(question.available_languages, propLanguage));
  }, [question]);

  useEffect(() => {
    if (language && language.length) {
      setQuestionText(getQuestionText(question.text, language, t('questionTableView.missingQuestionText')));
    }
  }, [language, t]);

  if (!language) {
    return null;
  }

  return (
    <div className="flex max-w-[50vw] flex-col justify-between gap-4 py-1 text-wrap">
      <div>
        <QuestionShortDescription
          shortDescription={question.short_description}
          fallbackText={t('questionTableView.noShortDescription')}
        />
      </div>

      <ShortableText text={questionText ?? ''} textLength={100} />

      <PreviewQuestion questionConfig={questionConfig} language={language} />
    </div>
  );
};

export default QuestionTableView;
